export const organizationConnectionStatuses = [
    "pending",
    "active",
    "rejected",
    "suspended",
    "disconnected",
];
export const organizationConnectionPolicies = [
    "open",
    "approvalRequired",
    "invitationOnly",
    "validated",
];
export const organizationConnectionValidationStatuses = [
    "not_required",
    "missing",
    "failed",
    "passed",
];
export const organizationConnectionInvitationStatuses = [
    "not_required",
    "presented",
    "accepted",
    "rejected",
];
export function buildOrganizationConnectionId(personId, organizationId) {
    return `${personId}_${organizationId}`;
}
export function organizationConnectionDocumentPath(personId, organizationId) {
    return `organizationConnections/${personId}/organizations/${organizationId}`;
}
export function organizationConnectionProjectionPath(organizationId, personId) {
    return `organizations/${organizationId}/connectionIndex/${personId}`;
}
export function buildBasicOrganizationConnectionRecord(input) {
    const active = input.status === "active";
    const roles = input.roles ?? (active ? ["connected_person"] : []);
    return {
        access: {
            basicConnectedPerson: active && roles.includes("connected_person"),
        },
        approvedAt: active ? input.timestamp : null,
        connectionId: buildOrganizationConnectionId(input.personId, input.organizationId),
        connectionType: "basic_connected_person",
        createdAt: input.timestamp,
        disconnectedAt: input.status === "disconnected" ? input.timestamp : null,
        invitation: {
            acceptedAt: null,
            invitationId: null,
            status: "not_required",
            tokenHash: null,
            ...input.invitation,
        },
        migration: {
            legacyFirebaseUid: null,
            legacySource: null,
            migratedAt: null,
            ...input.migration,
        },
        organizationId: input.organizationId,
        personId: input.personId,
        rejectedAt: input.status === "rejected" ? input.timestamp : null,
        requestedAt: input.timestamp,
        roles,
        source: input.source,
        status: input.status,
        suspendedAt: input.status === "suspended" ? input.timestamp : null,
        uid: input.personId,
        umidSnapshot: input.umidSnapshot ?? null,
        updatedAt: input.timestamp,
        validation: {
            method: null,
            reference: null,
            status: "not_required",
            validatedAt: null,
            ...input.validation,
        },
    };
}
export function buildOrganizationConnectionProjection(connection) {
    return {
        access: connection.access,
        approvedAt: connection.approvedAt,
        connectionId: connection.connectionId,
        connectionType: connection.connectionType,
        createdAt: connection.createdAt,
        disconnectedAt: connection.disconnectedAt,
        organizationId: connection.organizationId,
        personId: connection.personId,
        rejectedAt: connection.rejectedAt,
        requestedAt: connection.requestedAt,
        roles: connection.roles,
        source: connection.source,
        status: connection.status,
        suspendedAt: connection.suspendedAt,
        uid: connection.uid,
        umidSnapshot: connection.umidSnapshot,
        updatedAt: connection.updatedAt,
    };
}
export function canGrantBasicOrganizationAccess(connection) {
    if (!isRecord(connection)) {
        return false;
    }
    return (connection.status === "active" &&
        isRecord(connection.access) &&
        connection.access.basicConnectedPerson === true);
}
export function policyFromPublicDirectoryValue(value) {
    return value === "approval_required" ? "approvalRequired" : "open";
}
export function evaluateTestConnectionValidation(payload, settings) {
    if (!isRecord(payload)) {
        return {
            accepted: false,
            method: null,
            reference: null,
            status: "missing",
        };
    }
    const method = typeof payload.method === "string" ? payload.method : null;
    const value = typeof payload.value === "string" ? payload.value.trim() : "";
    if (!method || !value) {
        return {
            accepted: false,
            method,
            reference: null,
            status: "missing",
        };
    }
    if (method === "testCode" && settings.testCode !== null && value === settings.testCode) {
        return {
            accepted: true,
            method,
            reference: "testCode",
            status: "passed",
        };
    }
    return {
        accepted: false,
        method,
        reference: method,
        status: "failed",
    };
}
export function validateOrganizationConnectionRecord(connection) {
    const errors = [];
    if (!isRecord(connection)) {
        return { errors: ["Connection must be an object."], valid: false };
    }
    const personId = stringValue(connection.personId);
    const organizationId = stringValue(connection.organizationId);
    const connectionId = stringValue(connection.connectionId);
    if (!isFirestoreSafeDocumentId(personId)) {
        errors.push("Connection personId must be a Firestore-safe document ID.");
    }
    if (!isFirestoreSafeDocumentId(organizationId)) {
        errors.push("Connection organizationId must be Firestore-safe.");
    }
    if (connectionId !== buildOrganizationConnectionId(personId, organizationId)) {
        errors.push("Connection ID must be deterministic from personId and organizationId.");
    }
    if (!organizationConnectionStatuses.includes(connection.status)) {
        errors.push("Connection status must be supported.");
    }
    if (connection.connectionType !== "basic_connected_person") {
        errors.push("Connection type must be basic_connected_person for EXP-005.");
    }
    if (!isRecord(connection.access) || typeof connection.access.basicConnectedPerson !== "boolean") {
        errors.push("Connection access.basicConnectedPerson is required.");
    }
    if (!Array.isArray(connection.roles) ||
        !connection.roles.every((role) => typeof role === "string")) {
        errors.push("Connection roles compatibility field must be an array of strings.");
    }
    if (connection.status === "active" && !canGrantBasicOrganizationAccess(connection)) {
        errors.push("Active connection must grant basic connected-person access.");
    }
    if (connection.status !== "active" && canGrantBasicOrganizationAccess(connection)) {
        errors.push("Only active connections may grant basic connected-person access.");
    }
    if ("platformRole" in connection || "admin" in connection || "permissions" in connection) {
        errors.push("Connection must not contain platform/admin/permission shortcuts.");
    }
    return {
        errors,
        valid: errors.length === 0,
    };
}
function isFirestoreSafeDocumentId(value) {
    return value.length > 0 && value.length <= 150 && !value.includes("/");
}
function stringValue(value) {
    return typeof value === "string" ? value.trim() : "";
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
